import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./EyesOnlyPaywall.module.scss"

/**
 * EyesOnlyPaywall (`/eyesonly`) — an original, self-contained ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — but told through a
 * language the security cluster has never used: government-grade CLASSIFICATION.
 * Your memories are a classified file. To the world they are REDACTED. To one
 * person — you — they are CLEARED.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Your life, classified. Cleared for one person." The hero is a dossier — a
 * contact-sheet of your memories sitting under heavy black redaction bars,
 * corner tick-marks and a red REDACTED stamp. Tap AUTHENTICATE and a clearance
 * scanline sweeps top-to-bottom; as it passes each row the redaction bars
 * retract, the photo warms back to full colour, and a green CLEARED stamp thunks
 * home. Your real safeguards stamp GRANTED one by one as your credentials clear.
 * Tap again to re-classify and watch the bars slam back — because the file is
 * only ever open under your clearance.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from its security neighbours (Static cipher-noise, Ward
 * crest, Hallmark seal, OnlyYou keypad, Vault Door, Keyholder, Perimeter
 * geofence, Cipher, PlainSight, Decoy, Deep Freeze, Standby console):
 * Layout      : a CLASSIFIED DOSSIER — a bordered case file with a contact-sheet
 *               of redacted memory frames, clearance readout and credential
 *               manifest. Not a wall, a crest, a certificate, a keypad or a door.
 * Interaction : AUTHENTICATE — a single decisive tap fires an auto clearance
 *               scan (top→bottom); tap again to re-classify. New to the set:
 *               not a drag-slider (Static), not press-and-hold-converge (Ward),
 *               not a tilt-seal (Hallmark), not a keypad (OnlyYou), not a dial.
 * Metaphor    : SECURITY CLEARANCE / REDACTION — censorship bars, EYES-ONLY,
 *               DENIED→GRANTED. Not encryption noise, not a lock, not a vault.
 * Motion      : DECLASSIFY — a scanline sweep, redaction bars sliding out row by
 *               row, stamps thunking, credentials clearing in sequence.
 * Feature     : the real protections are CREDENTIALS on your clearance manifest;
 *               each stamps GRANTED as the scan reaches it.
 * Value frame : SOLE-AUTHORISATION / exclusivity — exactly one clearance exists
 *               for this file, and it is yours. (Not Static's contrast frame.)
 * CTA         : COMMITMENT — "Clear my vault for a year"; yearly = a full year of
 *               clearance, featured and default.
 * Register    : confident, insider, a little clandestine — empowered, not
 *               fearful; you hold the only clearance in the world.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock
 * with Face ID / Touch ID / PIN · encrypted Cloud Vault · Decoy Vault · break-in
 * snapshots · app disguise). "Classified/redacted" is an honest visual metaphor
 * for private, access-controlled photos — the copy states plainly that photos
 * are locked with your Face ID / code and encrypted in your Cloud Vault, open to
 * you and no one else. No countdown, no fabricated scarcity, no data-loss threat
 * (this is onboarding — nothing to lose). "Save 79%" is real arithmetic from
 * live product data. Yearly is the featured, default, most prominent price;
 * monthly is small and secondary. The 7-day free trial is the real trial.
 * Renders on built-in debug product data with bundled photos — no native
 * injection, no external egress.
 */

// The real Private Photo Vault safeguards — presented as credentials on your
// clearance manifest. Each stamps GRANTED as the scan reaches its row.
const CREDENTIALS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know",
    at: 0.12,
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of every memory",
    at: 0.3,
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless, empty vault",
    at: 0.48,
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
    at: 0.66,
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the whole vault behind an ordinary app",
    at: 0.84,
  },
]

// The dossier contact-sheet. Bundled, self-contained assets (reused from the
// repo). `y` is each frame's vertical centre (0=top,1=bottom) so the clearance
// scanline declassifies the sheet top-to-bottom as it sweeps.
const FRAMES = [
  {
    src: "/eyesonly/m1.jpg",
    col: "span 3",
    row: "span 2",
    y: 0.14,
    ref: "A-01",
  },
  {
    src: "/eyesonly/m7.jpg",
    col: "span 3",
    row: "span 1",
    y: 0.12,
    ref: "A-02",
  },
  {
    src: "/eyesonly/m3.jpg",
    col: "span 3",
    row: "span 1",
    y: 0.36,
    ref: "B-04",
  },
  {
    src: "/eyesonly/m2.jpg",
    col: "span 2",
    row: "span 1",
    y: 0.5,
    ref: "B-06",
  },
  {
    src: "/eyesonly/m5.jpg",
    col: "span 2",
    row: "span 1",
    y: 0.52,
    ref: "C-02",
  },
  {
    src: "/eyesonly/m9.jpg",
    col: "span 2",
    row: "span 1",
    y: 0.54,
    ref: "C-07",
  },
  {
    src: "/eyesonly/m4.jpg",
    col: "span 3",
    row: "span 1",
    y: 0.74,
    ref: "D-01",
  },
  {
    src: "/eyesonly/m8.jpg",
    col: "span 3",
    row: "span 1",
    y: 0.86,
    ref: "D-05",
  },
]

const SWEEP_MS = 1500

const EyesOnlyPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Match the safe-area insets to the ink canvas so the classified surface runs
  // edge to edge instead of sitting inside white status-bar bands.
  useEffect(() => {
    actions.setHeaderInsetColor("#10151d")
    actions.setFooterInsetColor("#090c11")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── clearance progress: 0 = fully redacted, 1 = fully declassified ─────────
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)

  const setProg = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v))
    progressRef.current = c
    setProgress(c)
  }, [])

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  const animateTo = useCallback(
    (target: number, durationMs: number) => {
      stopRaf()
      const from = progressRef.current
      const start = performance.now()
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / durationMs)
        const eased = t * t * (3 - 2 * t) // smootherstep
        setProg(from + (target - from) * eased)
        if (t < 1) raf.current = requestAnimationFrame(step)
        else raf.current = null
      }
      raf.current = requestAnimationFrame(step)
    },
    [stopRaf, setProg],
  )

  const cleared = progress >= 0.999
  const scanning = raf.current != null

  // AUTHENTICATE — the single-tap hero interaction. Declassify, or re-classify.
  const authenticate = useCallback(() => {
    userActed.current = true
    if (progressRef.current >= 0.5) animateTo(0, SWEEP_MS * 0.7)
    else animateTo(1, SWEEP_MS)
  }, [animateTo])

  // Auto-run one clearance sweep on mount so the payoff reads at a glance.
  useEffect(() => {
    if (reduceMotion) {
      setProg(1)
      return
    }
    const t = window.setTimeout(() => {
      if (!userActed.current) animateTo(1, SWEEP_MS)
    }, 620)
    return () => {
      window.clearTimeout(t)
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const p = progress
  const scanTop = `${p * 100}%`
  const grantedCount = CREDENTIALS.filter((c) => p >= c.at).length

  return (
    <div className={`${styles.root} ${cleared ? styles.isCleared : ""}`}>
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Classification · Private
          </span>
          <h1 className={styles.title}>
            Your life, <span className={styles.redactWord}>classified.</span>
            <br />
            Cleared for <span className={styles.titleAccent}>one person.</span>
          </h1>
        </section>

        {/* ── the dossier (hero) ─────────────────────────────────────────── */}
        <section className={styles.dossier}>
          <div className={styles.caseBar}>
            <span className={styles.caseLabel}>
              CASE&nbsp;<b>· YOUR&nbsp;MEMORIES</b>
            </span>
            <span
              className={`${styles.stamp} ${
                cleared ? styles.stampCleared : styles.stampRedacted
              }`}
            >
              {cleared ? "CLEARED" : "REDACTED"}
            </span>
          </div>

          <div className={styles.sheet} aria-hidden="true">
            {FRAMES.map((f, i) => {
              // each frame declassifies as the scanline passes its centre
              const local = Math.max(0, Math.min(1, (p - (f.y - 0.14)) / 0.28))
              return (
                <div
                  key={i}
                  className={styles.frame}
                  style={{
                    gridColumn: f.col,
                    gridRow: f.row,
                    ["--local" as any]: local,
                  }}
                >
                  <div
                    className={styles.framePhoto}
                    style={{ backgroundImage: `url(${f.src})` }}
                  />
                  <div className={styles.frameTint} />
                  {/* redaction bars — slide out as the frame clears */}
                  <div className={styles.bars}>
                    <span className={styles.bar} data-b="0" />
                    <span className={styles.bar} data-b="1" />
                    <span className={styles.bar} data-b="2" />
                    <span className={styles.bar} data-b="3" />
                  </div>
                  <span className={styles.frameRef}>{f.ref}</span>
                  <span className={styles.frameTick} aria-hidden="true" />
                </div>
              )
            })}

            {/* clearance scanline */}
            <div
              className={styles.scan}
              style={{
                top: scanTop,
                opacity: scanning ? 1 : cleared ? 0 : 0.55,
              }}
              aria-hidden="true"
            >
              <span className={styles.scanGlow} />
              <span className={styles.scanTag}>
                {cleared ? "" : "SCANNING"}
              </span>
            </div>
            <div className={styles.grain} aria-hidden="true" />
          </div>

          {/* clearance readout */}
          <div className={styles.readout} aria-live="polite">
            <span className={styles.readItem}>
              <span className={styles.readKey}>ACCESS</span>
              <span
                className={`${styles.readVal} ${
                  cleared ? styles.valOk : styles.valNo
                }`}
              >
                {cleared ? "GRANTED" : p > 0.02 ? "AUTHORISING…" : "DENIED"}
              </span>
            </span>
            <span className={styles.readItem}>
              <span className={styles.readKey}>CLEARANCE</span>
              <span className={styles.readVal}>EYES&nbsp;ONLY · YOU</span>
            </span>
          </div>
        </section>

        {/* ── AUTHENTICATE (the interaction) ─────────────────────────────── */}
        <button
          className={`${styles.authBtn} ${cleared ? styles.authClear : ""}`}
          onClick={authenticate}
          aria-pressed={cleared}
        >
          <span className={styles.authRing} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18">
              {/* fingerprint / biometric mark */}
              <path
                d="M12 4.5c-3 0-5.4 2.4-5.4 5.4v2.3M12 8c-1.1 0-2 .9-2 2v3.4M12 11.4v4.1M15.9 10c0-2.1-1.7-3.9-3.9-3.9M17.4 12.3V10M8.6 15.5c.2.9.5 1.7.9 2.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className={styles.authText}>
            {cleared ? "Tap to re-classify" : "Authenticate — clear my file"}
          </span>
          <span className={styles.authHint}>
            {cleared ? "Locked to you" : "Face ID · Touch ID · your code"}
          </span>
        </button>

        {/* ── credentials manifest (real safeguards) ─────────────────────── */}
        <ul className={styles.manifest}>
          <li className={styles.manifestHead}>
            <span>Your credentials</span>
            <span className={styles.manifestCount}>
              {grantedCount}/{CREDENTIALS.length} granted
            </span>
          </li>
          {CREDENTIALS.map((c) => {
            const on = p >= c.at
            return (
              <li
                key={c.key}
                className={`${styles.cred} ${on ? styles.credOn : ""}`}
              >
                <span className={styles.credIcon} aria-hidden="true">
                  <c.Icon width={16} height={16} />
                </span>
                <span className={styles.credText}>
                  <span className={styles.credName}>{c.name}</span>
                  <span className={styles.credLine}>{c.line}</span>
                </span>
                <span className={styles.credChip} aria-hidden="true">
                  {on ? "GRANTED" : "•"}
                </span>
              </li>
            )
          })}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Locked with your Face ID, Touch ID or code and encrypted in your Cloud
          Vault — your photos are open to you and no one else.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Full-year clearance
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or go month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Clear my vault — start ${trialDays}-day free trial`
              : "Clear my vault"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default EyesOnlyPaywall
